import React from "react";
import { blockWidth, fitTitle, textWidth } from "@/lib/chartText";

// The series are labelled directly, in a column to the right of the plot.
const seriesLabels = {
  restChina: "Resto de China",
  deepCent: "Resto de DeepCent",
  cdz: "ZDC",
};
// The size the labels render at on a desktop viewport; see the class on
// each label below, where the md breakpoint wins over the sm one.
const seriesLabelFontSize = 16;
const seriesLabelGap = 10;

export default function ChinaComputeCentralization() {
  // Set dimensions and margins - increased height for better data resolution
  const width = 1000;
  const height = 700;
  // The right margin is the column the labels sit in, so it has to be as wide
  // as the longest of them: translated labels run past the English ones.
  const labelColumnWidth =
    seriesLabelGap + blockWidth(Object.values(seriesLabels), seriesLabelFontSize);
  const margin = { top: 0, right: Math.max(165, labelColumnWidth), bottom: 60, left: 0 };
  const innerWidth = width - margin.left - margin.right;
  const innerHeight = height - margin.top - margin.bottom;

  // Month labels (shortened for display)
  const months = [
    "Dic 2025", "Ene 2026", "Feb 2026", "Mar 2026", "Abr 2026", "May 2026", "Jun 2026",
    "Jul 2026", "Ago 2026", "Sep 2026", "Oct 2026", "Nov 2026", "Dic 2026",
    "Ene 2027", "Feb 2027", "Mar 2027", "Abr 2027", "May 2027", "Jun 2027",
    "Jul 2027", "Ago 2027", "Sep 2027", "Oct 2027", "Nov 2027", "Dic 2027"
  ];
  const febIndex = 14;
  // Data arrays from oldPlots.py (FIG 8 data)
  const cdzData = [
    0, 0, 0, 0, 0, 0, 0,
    5e25, 1e26, 2.24e26, 4.12e26, 7.83e26, 1.15e27, 1.52e27, 1.89e27, 2.26e27, 2.6e27, 2.98e27,
    3.38e27, 3.82e27, 4.3e27, 4.78e27, 5.3e27, 5.86e27, 6.46e27
  ];

  const restOfDeepCentData = [
    2.13e26, 2.44e26, 2.79e26, 3.18e26, 3.64e26, 4.16e26, 4.75e26,
    4.93e26, 1.32e27, 1.4e27, 1.44e27, 1.5e27, 1.56e27, 1.69e27, 1.83e27, 1.86e27, 1.9e27,
    1.94e27, 1.99e27, 2.04e27, 2.09e27, 2.14e27, 2.2e27, 2.26e27, 2.33e27
  ];

  const restOfChinaData = [
    1.29e27, 1.38e27, 1.48e27, 1.58e27, 1.68e27, 1.79e27, 1.91e27,
    2.03e27, 1.36e27, 1.38e27, 1.38e27, 1.21e27, 1.05e27, 8.55e26, 6.66e26, 6.1e26, 6.09e26,
    6.03e26, 5.92e26, 5.75e26, 5.5e26, 5.71e26, 5.92e26, 6.13e26, 6.33e26
  ];

  // Calculate cumulative values for stacking
  const cdzLayer = cdzData;
  const deepCentTotalLayer = cdzData.map((d, i) => d + restOfDeepCentData[i]);
  const totalLayer = deepCentTotalLayer.map((d, i) => d + restOfChinaData[i]);

  // Maximum value for scaling
  const maxTotal = Math.max(...totalLayer);

  // Scales: x based on index, y based on value
  const xScale = (i: number) => margin.left + (i / (months.length - 1)) * innerWidth;
  const yScale = (value: number) => margin.top + innerHeight - (value / maxTotal) * innerHeight;

  // Generate x-values for all data points
  const xValues = months.map((_, i) => xScale(i));

  // Helper to format numbers in scientific notation
  const formatSci = (value: number): string => {
    if (value === 0) return "0";
    const exponent = Math.floor(Math.log10(value));
    const coefficient = value / Math.pow(10, exponent);
    return coefficient.toFixed(1) + "×10^" + exponent;
  };

  // Generate y-axis ticks (fewer ticks for cleaner presentation)
  const yTicks = Array.from({ length: 5 }, (_, i) => (i * maxTotal) / 4);
  
  // Choose select x-axis tick indices for clarity
  const xTickIndices = [0, 6, 12, 18, 24];
  
  // Line generation helper
  const generateLinePath = (data: number[]): string => {
    return data
      .map((d, i) => `${i === 0 ? "M" : "L"} ${xScale(i)} ${yScale(d)}`)
      .join(" ");
  };

  // Area generation helper (for stacked areas)
  const generateAreaPath = (topData: number[], bottomData?: number[]): string => {
    const topPath = topData.map((d, i) => `${i === 0 ? "M" : "L"} ${xScale(i)} ${yScale(d)}`).join(" ");
    
    if (!bottomData) {
      // If no bottom data, close the path to the baseline
      return `${topPath} L ${xScale(months.length - 1)} ${yScale(0)} L ${xScale(0)} ${yScale(0)} Z`;
    }
    
    // Create bottom path in reverse order
    const bottomPathReverse = Array.from({length: bottomData.length}, (_, i) => {
      const reverseIndex = bottomData.length - 1 - i;
      return `L ${xScale(reverseIndex)} ${yScale(bottomData[reverseIndex])}`;
    }).join(" ");
    
    return `${topPath} ${bottomPathReverse} Z`;
  };

  // Create paths for stacked areas
  const cdzArea = generateAreaPath(cdzLayer);
  const deepCentArea = generateAreaPath(deepCentTotalLayer, cdzLayer);
  const restOfChinaArea = generateAreaPath(totalLayer, deepCentTotalLayer);

  // Also create line paths for the boundaries
  const cdzLine = generateLinePath(cdzLayer);
  const deepCentLine = generateLinePath(deepCentTotalLayer);
  const totalLine = generateLinePath(totalLayer);

  // More military-style muted colors
  const colors = {
    cdz: "var(--accent)",         // Military green
    deepCent: "rgba(0,0,0,0.6)",    // Muted navy 
    restChina: "rgba(0,0,0,0.4)",   // Desaturated tan
    axis: "#444444",        // Dark gray for axis
    text: "#333333"         // Dark gray text
  };

  // Helper to calculate the derivative of a curve at a specific point
  const getGrowthRate = (data: number[], index: number): number => {
    if (index <= 0 || index >= data.length - 1) return 0;
    return (data[index + 1] - data[index - 1]) / 2;
  };

  // Find a good position for labels - near the end of the chart where slopes aren't too steep
  const labelIndex = totalLayer.length - 1; // Near the end but not at the very end

  const title = "Centralización del poder de cómputo en China, 2025-2027";
  // The title has nowhere to wrap to, and must not run into the column of
  // series labels on the right, so an over-long translation is set smaller.
  const titleStyle = fitTitle(title, width - margin.right - 10);

  // The subtitle shares its line with the mini sparkline, which is placed
  // after it rather than at a fixed offset.
  const subtitle = "Porcentaje creciente del poder de cómputo total en la ZDC:";
  const subtitleWidth = textWidth(subtitle, 16);

  return (
    <div className="w-full relative overflow-hidden mt-8 mb-8">
      <svg
        width="100%"
        preserveAspectRatio="xMidYMid meet"
        viewBox={`0 0 ${width} ${height}`}
      >
        {/* Chart Title - Military report style */}
        <text
          x={0}
          y={40}
          fontSize={titleStyle.fontSize}
          fontFamily="monospace"
          className={
            titleStyle.fits
              ? "text-[28px] font-mono md:text-[24px] sm:text-[20px]"
              : "font-mono"
          }
          fill={colors.text}
        >
          {title}
        </text>

        {/* Mini chart moved next to title */}
        <text
          x={0}
          y={80}
          className="text-[16px] font-mono md:text-[16px] sm:text-[16px]"
          fill={colors.text}
        >
          {subtitle}
        </text>
        
        {/* Mini chart showing CDZ percentage growth - repositioned to right of title */}
        {(() => {
          const miniWidth = 160;
          const miniHeight = 25;
          const miniX = Math.max(margin.left + 380, subtitleWidth + 12);
          const miniY = 60;
          
          // Calculate percentage of CDZ in total
          const percentages = cdzLayer.map((val, i) => 
            totalLayer[i] > 0 ? (val / totalLayer[i] * 100) : 0
          );
          
          // Create mini chart path
          const miniPath = percentages
            .map((p, i) => {
              const x = miniX + (i / (percentages.length - 1)) * miniWidth;
              const y = miniY + miniHeight - (p / 100) * miniHeight;
              return `${i === 0 ? 'M' : 'L'} ${x} ${y}`;
            })
            .join(' ');
            
          return (
            <g>
              <path 
                d={miniPath} 
                stroke={colors.cdz} 
                fill="none" 
                strokeWidth="1.5" 
              />
              <text 
                x={miniX + miniWidth + 10} 
                y={miniY + 12} 
                className="text-[16px] font-mono md:text-[16px] sm:text-[14px]"
                fill={colors.text}
              >
                {`0→70%`}{/* {`0→${percentages[percentages.length - 1].toFixed(0).}%`} */}
              </text>
            </g>
          );
        })()}

        {/* X-axis modest ticks and labels */}
        {xTickIndices.map((index) => {
          const x = xValues[index];
          const shift = (x === 0) ? 0 : 40;
          return (
            <g key={index}>
              <line 
                x1={x} 
                y1={height - margin.bottom} 
                x2={x} 
                y2={margin.top + 20} 
                stroke={colors.axis}
                strokeWidth="0.5" 
                strokeDasharray="5,5"
                opacity="0.15"
              />
              <text 
                x={x-shift} 
                y={height - margin.bottom + 20} 
                textAnchor="start" 
                className="text-[18px] font-mono md:text-[18px] sm:text-[18px]"
                fill={colors.text}
              >
                {months[index]}
              </text>
            </g>
          );
        })}

        {/* Stacked areas (filled with subtle colors) */}
        <path 
          d={restOfChinaArea} 
          fill={colors.restChina} 
          fillOpacity={0.15} 
          stroke="none"
        />
        <path 
          d={deepCentArea} 
          fill={colors.deepCent} 
          fillOpacity={0.25} 
          stroke="none"
        />
        <path 
          d={cdzArea} 
          fill={colors.cdz} 
          fillOpacity={0.45} 
          stroke="none"
        />

        {/* Boundary lines for clarity (thin, precise) */}
        <path d={totalLine} fill="none" stroke={colors.restChina} strokeWidth="1.5" strokeOpacity={0.5} />
        <path d={deepCentLine} fill="none" stroke={colors.deepCent} strokeWidth="1.5" strokeOpacity={0.5}/>
        <path d={cdzLine} fill="none" stroke={colors.cdz} strokeWidth="1.5" />

        {/* Data points at important intervals (not every point to avoid clutter) */}
        {[7, 10, 13, 16, 19, 22, 24].map(i => (
          <g key={`points-${i}`}>
            <circle 
              cx={xScale(i)} 
              cy={yScale(totalLayer[i])} 
              r={2.5} 
              fill={colors.restChina} 
              stroke="#fff" 
              strokeWidth="0.5"
            />
            <circle 
              cx={xScale(i)} 
              cy={yScale(deepCentTotalLayer[i])} 
              r={2.5} 
              fill={colors.deepCent} 
              stroke="#fff" 
              strokeWidth="0.5"
            />
            <circle 
              cx={xScale(i)} 
              cy={yScale(cdzLayer[i])} 
              r={2.5} 
              fill={colors.cdz} 
              stroke="#fff" 
              strokeWidth="0.5"
            />
          </g>
        ))}

        {/* Direct labeling with leader lines - positioned away from the chart */}
        {/* Total China label */}
        <text 
          x={width - margin.right + seriesLabelGap} 
          y={yScale(totalLayer[labelIndex] - (totalLayer[labelIndex] - deepCentTotalLayer[labelIndex]) / 2)} 
          textAnchor="start" 
          dominantBaseline="middle"
          className="text-[18px] font-mono md:text-[16px] sm:text-[14px]"
          fill={colors.restChina}
        >
          {seriesLabels.restChina}
        </text>

        {/* Deep Cent label */}
        <text 
          x={width - margin.right + seriesLabelGap} 
          y={yScale(deepCentTotalLayer[labelIndex] - (deepCentTotalLayer[labelIndex] - cdzLayer[labelIndex]) / 2)} 
          textAnchor="start" 
          dominantBaseline="middle"
          className="text-[18px] font-mono md:text-[16px] sm:text-[14px]"
          fill={colors.deepCent}
        >
          {seriesLabels.deepCent}
        </text>

        {/* CDZ label */}
        <text 
          x={width - margin.right + seriesLabelGap} 
          y={yScale(cdzLayer[labelIndex] / 2)} 
          textAnchor="start" 
          dominantBaseline="middle"
          className="text-[18px] font-mono md:text-[16px] sm:text-[14px]"
          fill={colors.cdz}
        >
          {seriesLabels.cdz}
        </text>

        {/* Special marker for March 2027 (50% CDZ) */}
        <line 
          x1={xScale(febIndex) } 
          y1={height - margin.bottom} 
          x2={xScale(febIndex)} 
          y2={yScale(cdzLayer[febIndex])} 
          stroke={colors.cdz}
          strokeWidth="1" 
          strokeDasharray="3,3"
          opacity="0.5"
        />
        <text 
          x={xScale(febIndex)+70} 
          y={height - margin.bottom - 10} 
          textAnchor="middle" 
          className="text-[16px] font-mono md:text-[16px] sm:text-[16px]"
          fill={colors.deepCent}
        >
          Feb 2027 (40%)
        </text>
        <circle 
          cx={xScale(febIndex)} 
          cy={yScale(cdzLayer[febIndex])} 
          r={3.5} 
          fill={colors.cdz} 
          stroke="#fff" 
          strokeWidth="0.75"
        />
      </svg>
    </div>
  );
}